import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";

import { PackTeaser } from "@/components/home/PackTeaser";
import { PriceList } from "@/components/home/PriceList";
import { Button } from "@/components/ui/button";
import { COMPANY, WHATSAPP_URL } from "@/lib/company";
import {
  AD_SPEND_NOTE,
  AGENCY_OFFER,
  AUDIT_OFFER,
  DEVELOPER_OFFER,
  LAUNCH_OFFER,
  FIT_REVIEW_NOTE,
  MONTHLY_OFFERS,
  MONTHLY_TERMS,
  OFFERS,
  ONE_OFF_OFFERS,
} from "@/lib/pricing";
import { SiteFooter, SiteHeader } from "@/components/layout/SiteChrome";

const BASE_URL = "https://adkiro.com";
const PAGE_URL = `${BASE_URL}/meta-ads-pricing`;
const TITLE = "Meta Ads Services & Pricing for Property | Adkiro";
const DESCRIPTION = `Account reviews ${AUDIT_OFFER.price}, property launches ${LAUNCH_OFFER.price}, and monthly management ${AGENCY_OFFER.price.toLowerCase()} for agencies and ${DEVELOPER_OFFER.price.toLowerCase()} for developers. Pay online by card, Apple Pay or Google Pay.`;

const PRICING_FAQS = [
  {
    q: "When do I pay?",
    a: "After the free review, once Kristian has confirmed in writing which service fits, or straight away if you already know which service you need. One-off services are paid online before work starts. Monthly plans are paid online too and renew by card each month; an invoice is available on request.",
  },
  {
    q: "How can I pay?",
    a: "By card, Apple Pay or Google Pay through Stripe, from any country. Prices are in US dollars. Stripe emails a receipt straight away; if your company needs an invoice with its details, reply to the receipt.",
  },
  {
    q: "Is the advertising budget included?",
    a: `No. ${AD_SPEND_NOTE} You agree the daily budget in the proposal and can change it at any time.`,
  },
  {
    q: "Is the $950 account review credited?",
    a: "Yes. When the review leads into monthly management, the full $950 is credited against the first month. The written review, 90-day roadmap and handover are yours either way.",
  },
  {
    q: "What is not included?",
    a: "Photography, video shoots, 3D renders and landing pages. The creatives are made from the photos and material you already have; anything more is quoted separately before it starts.",
  },
  {
    q: "How long is the contract?",
    a: `One-off services have no contract. ${MONTHLY_TERMS}`,
  },
];

export const Route = createFileRoute("/meta-ads-pricing")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: PAGE_URL },
      { property: "og:type", content: "website" },
      { property: "og:image", content: `${BASE_URL}/logo.png?v=20260907` },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
    ],
    links: [{ rel: "canonical", href: PAGE_URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Service",
              "@id": `${PAGE_URL}#service`,
              name: "Meta advertising for property",
              serviceType: "Facebook and Instagram advertising for real estate",
              url: PAGE_URL,
              provider: { "@id": `${BASE_URL}/#organization` },
              areaServed: ["United States", "Europe", "United Arab Emirates"],
              description:
                "Account reviews, property launch campaigns and monthly Meta Ads management for property agencies, brokers and developers.",
              offers: OFFERS.map((offer) => ({
                "@type": "Offer",
                name: offer.name,
                ...(offer.schemaPrice !== undefined && {
                  price: offer.schemaPrice,
                  priceCurrency: "USD",
                }),
                description: `${offer.fit}. ${offer.blurb}`,
                url: `${PAGE_URL}#${offer.id}`,
                ...(offer.cadence === "/month" &&
                  offer.schemaPrice !== undefined && {
                    priceSpecification: {
                      "@type": "UnitPriceSpecification",
                      minPrice: offer.schemaPrice,
                      priceCurrency: "USD",
                      unitText: "month",
                    },
                  }),
              })),
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: `${BASE_URL}/` },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: "Services & pricing",
                  item: PAGE_URL,
                },
              ],
            },
            {
              "@type": "FAQPage",
              mainEntity: PRICING_FAQS.map((faq) => ({
                "@type": "Question",
                name: faq.q,
                acceptedAnswer: { "@type": "Answer", text: faq.a },
              })),
            },
          ],
        }),
      },
    ],
  }),
  component: MetaAdsPricingPage,
});

function MetaAdsPricingPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader tone="ink" />

      <main>
        <section data-tone="ink" className="tone-ink relative overflow-hidden">
          <div className="relative mx-auto max-w-5xl px-5 pb-16 pt-14 text-center sm:pt-20">
            <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground">
              <Link to="/" className="hover:text-foreground">
                Home
              </Link>{" "}
              <span aria-hidden="true">/</span> Services &amp; pricing
            </nav>
            <h1 className="mt-8 text-4xl font-bold leading-[1.05] sm:text-6xl">
              Services and prices
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg">
              Meta advertising for property agencies, brokers and developers in Dubai, Europe and
              the US. Start with a free review, or pay online on this page if you already know which
              service you need.
            </p>
            <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Button variant="brand" size="xl" asChild>
                <a href="/#audit">
                  Get a free review <ArrowRight className="size-4" />
                </a>
              </Button>
              <Button variant="outline" size="xl" asChild>
                <a href="#pay">Pay online</a>
              </Button>
            </div>
            <p className="mt-4 text-xs text-muted-foreground">{FIT_REVIEW_NOTE}</p>
          </div>
        </section>

        {/* #pay used to be a section of its own, listing every pay button a second time.
            Each service now carries its own, so the links to #pay (the header, the
            footer, messages already sent) land here instead. */}
        <section
          id="pay"
          aria-labelledby="one-off-heading"
          className="mx-auto max-w-7xl scroll-mt-16 px-5 py-16"
        >
          <div className="mb-12">
            <PackTeaser />
          </div>
          <h2
            id="one-off-heading"
            className="mb-4 text-xs uppercase tracking-[0.2em] text-muted-foreground"
          >
            One-off
          </h2>
          <PriceList offers={ONE_OFF_OFFERS} anchored expanded />

          <h2 className="mb-4 mt-14 text-xs uppercase tracking-[0.2em] text-muted-foreground">
            Monthly management
          </h2>
          <PriceList offers={MONTHLY_OFFERS} anchored expanded startAt={ONE_OFF_OFFERS.length} />
          <p className="mt-4 max-w-4xl text-xs leading-relaxed text-muted-foreground">
            Prices are in US dollars and exclude any applicable taxes. {MONTHLY_TERMS}{" "}
            {AD_SPEND_NOTE} Stripe takes the payment, by card, Apple Pay or Google Pay, and emails
            your receipt; card details never reach this site. Prefer an invoice?{" "}
            <a
              href={`mailto:${COMPANY.email}?subject=Invoice`}
              className="text-primary hover:text-foreground"
            >
              Email {COMPANY.email}
            </a>
            .
          </p>
        </section>

        <section className="mx-auto max-w-4xl px-5 py-16">
          <p className="text-xs uppercase tracking-[0.25em] text-primary">Pricing questions</p>
          <h2 className="mt-3 text-3xl font-bold sm:text-4xl">Before you pay</h2>
          <div className="mt-8 space-y-4">
            {PRICING_FAQS.map((faq) => (
              <article key={faq.q} className="glass rounded-2xl p-6">
                <h3 className="text-lg font-semibold">{faq.q}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{faq.a}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="mx-auto max-w-6xl px-5 pb-24 pt-8">
          <div className="glass relative overflow-hidden rounded-3xl px-6 py-14 text-center">
            <div className="pointer-events-none absolute inset-0 halo" />
            <div className="relative">
              <h2 className="text-3xl font-bold sm:text-4xl">Not sure which service fits?</h2>
              <p className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground">
                {FIT_REVIEW_NOTE}
              </p>
              <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
                <Button variant="brand" size="xl" asChild>
                  <a href="/#audit">
                    Get a free review <ArrowRight className="size-4" />
                  </a>
                </Button>
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
                >
                  Message on WhatsApp
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
